// Regras da importação de CRLV-e em lote: o que fazer com cada documento lido, em relação ao que já
// está no cadastro. Funções puras, sem I/O — a tela (telas/ImportarCrlv.jsx) cuida das chamadas à API.
//
// O caminho de um CRLV é sempre o mesmo:
//   documento lido -> cliente (do proprietário) -> veículo (da placa) -> cadastrar / completar / transferir
import { formatarPlaca } from './leituraVeiculo';
import { normalizar } from './comparacaoCadastro';

// Campos do veículo que o CRLV preenche, na ordem em que aparecem para o usuário
export const CAMPOS_DO_CRLV = [
  { campo: 'placa', rotulo: 'placa' },
  { campo: 'marcaModelo', rotulo: 'marca/modelo' },
  { campo: 'ano', rotulo: 'ano' },
  { campo: 'cor', rotulo: 'cor' },
  { campo: 'renavam', rotulo: 'RENAVAM' },
  { campo: 'chassi', rotulo: 'chassi' },
  { campo: 'motor', rotulo: 'nº do motor' },
];

// Os dados do veículo como eles entram no cadastro. A placa recebe o hífen porque é assim que o
// sistema a grava (e a busca no backend é por igualdade exata).
export function veiculoDoDocumento(lido) {
  const dados = {};
  for (const { campo } of CAMPOS_DO_CRLV) {
    const valor = (lido || {})[campo];
    if (valor) dados[campo] = campo === 'placa' ? formatarPlaca(valor) : valor;
  }
  return dados;
}

// Campos em que o documento discorda do cadastro: os dois preenchidos, com valores diferentes.
// Campo vazio no cadastro não é divergência — é lacuna, e completar lacuna não precisa de confirmação.
// Campo vazio no documento também não: o CRLV não apaga o que o cadastro já sabe.
export function divergencias(cadastro, lido) {
  const doDocumento = veiculoDoDocumento(lido);
  return CAMPOS_DO_CRLV
    .filter(({ campo }) => {
      const atual = normalizar(campo, (cadastro || {})[campo]);
      const novo = normalizar(campo, doDocumento[campo]);
      return atual !== '' && novo !== '' && atual !== novo;
    })
    .map(({ campo, rotulo }) => ({ campo, rotulo, atual: cadastro[campo], lido: doDocumento[campo] }));
}

// Lacunas que o documento preenche: o cadastro não tem, o documento tem. É o que o /complementar grava.
export function lacunas(cadastro, lido) {
  const doDocumento = veiculoDoDocumento(lido);
  return CAMPOS_DO_CRLV
    .filter(({ campo }) => normalizar(campo, (cadastro || {})[campo]) === '' && normalizar(campo, doDocumento[campo]) !== '')
    .map(({ campo, rotulo }) => ({ campo, rotulo, lido: doDocumento[campo] }));
}

// Divergência no cadastro do cliente. Do CRLV só saem nome e CPF/CNPJ, e o CPF/CNPJ é justamente como
// o cliente foi encontrado — então nunca diverge. Sobra o nome: grafia diferente, nome de casada, ou
// simplesmente um cadastro antigo desatualizado. O endereço e o resto do cadastro não são tocados,
// porque o documento não os traz.
export function divergenciasDoCliente(cadastro, lido) {
  const doDocumento = ((lido || {}).proprietario || {}).nome || '';
  const atual = (cadastro || {}).nome || '';
  if (!atual.trim() || !doDocumento.trim()) return [];
  const igual = atual.trim().toUpperCase() === doDocumento.trim().toUpperCase();
  return igual ? [] : [{ campo: 'nome', rotulo: 'nome', atual, lido: doDocumento }];
}

// O que fazer com o veículo encontrado (ou não) para este cliente:
//  'cadastrar'   — não existe na empresa: cria no cliente
//  'completar'   — já é deste cliente: completa o que falta
//  'transferir'  — existe, mas em outro cliente: precisa da confirmação do usuário
export function acaoParaVeiculo(veiculo, idCliente) {
  if (!veiculo || !veiculo.idVeiculo) return 'cadastrar';
  const dono = veiculo.cliente ? veiculo.cliente.idCliente : veiculo.idCliente;
  return dono && Number(dono) !== Number(idCliente) ? 'transferir' : 'completar';
}

// O que mandar para PUT /veiculos/{id}/complementar.
//
// Atenção: esse endpoint NÃO é "preenche só o que está vazio". Ele sobrescreve todo campo que chegar
// preenchido, e só ignora o que vem em branco (VeiculoService.complementarDados). Mandar o documento
// inteiro passaria por cima do cadastro — inclusive do que o usuário acabou de mandar manter.
// Por isso aqui vai só o que deve mesmo mudar: as lacunas do cadastro e os campos que o usuário
// escolheu substituir.
export function complementoDoDocumento(cadastro, lido, camposEscolhidos = []) {
  const doDocumento = veiculoDoDocumento(lido);
  const aPreencher = lacunas(cadastro, lido).map((l) => l.campo);
  const payload = {};
  for (const { campo } of CAMPOS_DO_CRLV) {
    if (!doDocumento[campo]) continue;
    if (aPreencher.includes(campo) || (camposEscolhidos || []).includes(campo)) payload[campo] = doDocumento[campo];
  }
  return payload;
}

// A placa é o único campo que o /complementar se recusa a trocar quando já existe (é a identidade do
// veículo). Só para trocá-la é preciso o PUT completo, que substitui o veículo inteiro.
export function exigeSubstituicaoDePlaca(cadastro, camposEscolhidos) {
  return (camposEscolhidos || []).includes('placa') && !!(cadastro || {}).placa;
}

// Payload de substituição para PUT /veiculos/{id}: parte do cadastro atual e troca só os campos que o
// usuário escolheu usar do documento. O cliente e os licenciamentos vão junto de propósito — o PUT
// substitui o veículo inteiro, e omiti-los desvincularia o veículo do cliente e apagaria a licença.
export function veiculoComEscolhas(cadastro, lido, camposEscolhidos, idCliente) {
  const doDocumento = veiculoDoDocumento(lido);
  const payload = {};
  for (const { campo } of CAMPOS_DO_CRLV) {
    const escolheuODocumento = (camposEscolhidos || []).includes(campo);
    const valor = escolheuODocumento && doDocumento[campo] ? doDocumento[campo] : cadastro[campo];
    if (valor) payload[campo] = valor;
  }
  payload.observacao = cadastro.observacao;
  payload.licenciamentos = Array.isArray(cadastro.licenciamentos) ? cadastro.licenciamentos : [];
  payload.cliente = { idCliente };
  return payload;
}

// Quais CRLV já anexados ao veículo são substituídos por este documento novo.
//
// O CRLV do ano vigente torna o do ano passado inútil: guardar os dois só enche o armazenamento. Mas
// apagar documento é irreversível, então a regra é estreita de propósito:
//
//  - só entram anexos que a própria importação marcou com exercício. Anexo antigo, sem essa marca,
//    pode ser uma CNH ou uma procuração digitalizada — o sistema não sabe, e não mexe;
//  - só exercício ANTERIOR ao que está chegando. Mesmo ano não substitui (é reenvio do mesmo
//    documento), e ano mais novo nunca é apagado por um mais velho chegando atrasado;
//  - a verificação silenciosa: o chassi do documento tem de bater com o do cadastro. A placa muda
//    (Mercosul, reemplacamento), o chassi não. Sem chassi de um dos lados, não substitui nada.
export function crlvsASubstituir(anexos, cadastro, lido) {
  const exercicioNovo = (lido || {}).exercicio;
  if (!exercicioNovo) return [];

  const chassiDoc = normalizar('chassi', (lido || {}).chassi);
  const chassiCadastro = normalizar('chassi', (cadastro || {}).chassi);
  if (!chassiDoc || chassiDoc !== chassiCadastro) return [];

  return (anexos || []).filter((a) => a && a.exercicio && Number(a.exercicio) < Number(exercicioNovo));
}

// Contagem para a barra de progresso e para o resumo final
export function resumoDaFila(fila) {
  const conta = (...status) => (fila || []).filter((i) => status.includes(i.status)).length;
  return {
    total: (fila || []).length,
    concluidos: conta('cadastrado', 'completado', 'transferir', 'erro', 'cancelado', 'atencao'),
    cadastrados: conta('cadastrado'),
    completados: conta('completado'),
    transferencias: conta('transferir'),
    falhas: conta('erro'),
  };
}
